import { createFileRoute } from "@tanstack/react-router";
import {
  Building,
  Cpu,
  Sparkles,
  FileCheck,
  FileSearch,
  Link as LinkIcon,
  Monitor,
  ScrollText,
  LockKeyhole,
  ShieldCheck,
  Users,
  Package,
} from "lucide-react";
import { SecondaryNav } from "@client/components/secondary-nav";
import { useAuthState } from "@tubo/store";
import { useGetPublicationReviewAccess } from "@tubo/api-models/hooks/publication-api.hooks";
import { usePermissions } from "@client/hooks/use-permissions";
import { useNavPermissions } from "@client/hooks/use-nav-permissions";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_app/tenant-settings")({
  component: TSettingsLayout,
});

const tSettingsItems = [
  {
    titleKey: "navigation.tenantInfo",
    to: "/tenant-settings/tenant-info" as const,
    icon: Building,
    permission: "team-info",
  },
  {
    titleKey: "navigation.team",
    to: "/tenant-settings/users" as const,
    icon: Users,
    permissions: ["team-users", "team-departments", "team-roles"],
  },
  {
    titleKey: "navigation.permissions",
    to: "/tenant-settings/permissions" as const,
    icon: ShieldCheck,
    permission: "team-share-governance",
  },
  {
    titleKey: "navigation.documentSecurity",
    to: "/tenant-settings/document-security" as const,
    icon: LockKeyhole,
    permission: "team-share-governance",
  },
  {
    titleKey: "navigation.aiModels",
    to: "/tenant-settings/ai-models" as const,
    icon: Sparkles,
    permission: "team-ai-models",
  },
  {
    titleKey: "navigation.parseEngines",
    to: "/tenant-settings/parse-engines" as const,
    icon: Cpu,
    permission: "team-parse-engines",
  },
  {
    titleKey: "navigation.docSettings",
    to: "/tenant-settings/doc-settings" as const,
    icon: FileSearch,
    permission: "team-doc-settings",
  },
  {
    titleKey: "navigation.dataSources",
    to: "/tenant-settings/data-sources" as const,
    icon: LinkIcon,
    permission: "team-data-sources",
  },
  {
    titleKey: "navigation.publications",
    to: "/tenant-settings/publications" as const,
    icon: FileCheck,
  },
  {
    titleKey: "navigation.industryPacks",
    to: "/tenant-settings/industry-packs" as const,
    icon: Package,
  },
  {
    titleKey: "navigation.auditLogs",
    to: "/tenant-settings/audit-logs" as const,
    icon: ScrollText,
    permission: "team-audit-logs",
  },
  {
    titleKey: "navigation.systemStatus",
    to: "/tenant-settings/system-status" as const,
    icon: Monitor,
    permission: "team-system-status",
  },
];

function TSettingsLayout() {
  const { t } = useTranslation("tenantSettings");
  const { user } = useAuthState();
  // 二级目录较窄,头部只显示租户名称(主侧栏仍显示「团队设置」),无租户名时兜底原标题
  const tenantName = user?.tenant?.name ?? "";
  const title = tenantName || t("navigation.settings");

  // 管理员专属入口（PF-13 配置即放行）：发布审核配置为指定审核人者可见；行业包仅管理员
  const { isTenantAdmin } = usePermissions();
  const { data: reviewAccess } = useGetPublicationReviewAccess();
  const canSeePublications = isTenantAdmin || !!reviewAccess?.canReview;

  // 后端一次返回当前用户有权访问的所有 key
  const permSet = useNavPermissions();

  const items = tSettingsItems
    .filter(
      (item) =>
        (item.to !== "/tenant-settings/publications" || canSeePublications) &&
        (item.to !== "/tenant-settings/industry-packs" || isTenantAdmin) &&
        (item.to !== "/tenant-settings/data-sources" || isTenantAdmin) &&
        (item.permission !== "team-share-governance" || isTenantAdmin),
    )
    .filter((item) => {
      if (item.permissions) {
        return item.permissions.some((permission) => permSet.has(permission));
      }
      if (!item.permission) return true;
      return permSet.has(item.permission);
    })
    .map((item) => ({ ...item, title: t(item.titleKey) }));

  return <SecondaryNav title={title} items={items} />;
}
